// Detect Safari browser
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { IVideoRoomTokenRequest } from 'vcs-common';

import { useEchoTest } from '@useEchoTest';

import { useChangeUserMutation, useGenerateVideoRoomTokenMutation } from '../../services/users.api';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { CustomSelect, Button, CloseButton, Spinner, Heading } from '../../shared/ui';
import MicrophoneLevelIndicator from './microphone-level-indicator';

export const MediaSettingsModal: FC<{
  changeSettings: (
    video: MediaDeviceInfo | undefined,
    audioOut: MediaDeviceInfo | undefined,
    audioIn: MediaDeviceInfo | undefined,
  ) => Promise<void>;
  setOpenSettings: (val: boolean) => void;
  isPsychologist: boolean;
  audioDevice?: MediaDeviceInfo | null;
  videoDevice?: MediaDeviceInfo | null;
}> = ({ setOpenSettings, changeSettings, isPsychologist, audioDevice, videoDevice }) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const userData = useAppSelector(selectUser);
  const selectedAudioOutputDeviceRef = useRef<MediaDeviceInfo>();

  const dispatch = useAppDispatch();
  const [changeUser] = useChangeUserMutation();
  const [generateToken] = useGenerateVideoRoomTokenMutation();

  const [isDevicesInitialized, setIsDevicesInitialized] = useState(false);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>(() => audioDevice?.label || '');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>(() => videoDevice?.label || '');
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState<MediaDeviceInfo>();
  const [isAudioMuted, setIsAudioMuted] = useState(true);
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [isRemoteLoading, setIsRemoteLoading] = useState(true);

  const generateRoomToken = useCallback(
    async (request: IVideoRoomTokenRequest) => {
      return await generateToken(request).unwrap();
    },
    [generateToken],
  );

  const { initialized, start, stop, changeAudioDevice, changeVideoDevice } = useEchoTest(
    localVideoRef,
    remoteVideoRef,
    generateRoomToken,
    () => setIsRemoteLoading(false),
  );

  // const { initialized, start, stop, changeAudioDevice, changeVideoDevice } = useLiveKitEchoTest(
  //   localVideoRef,
  //   remoteVideoRef,
  //   generateRoomToken,
  // );

  useEffect(() => {
    console.debug('Starting echo effect');
    const startEcho = async () => {
      if (initialized && isDevicesInitialized && selectedVideoDevice) {
        console.debug(`Starting echo with init ${initialized} selected video ${selectedVideoDevice}`);

        const selectedVideoDeviceId = selectedVideoDevice
          ? videoDevices?.find((d) => d.label === selectedVideoDevice)?.deviceId
          : undefined;
        const selectedAudioDeviceId = selectedAudioDevice
          ? audioDevices?.find((d) => d.label === selectedAudioDevice)?.deviceId
          : undefined;

        await start(selectedVideoDeviceId, selectedAudioDeviceId);
        setIsLocalLoading(false);
      }
    };

    startEcho();
  }, [
    audioDevices,
    initialized,
    isDevicesInitialized,
    selectedAudioDevice,
    selectedVideoDevice,
    start,
    stop,
    videoDevices,
  ]);

  const saveSettings = async () => {
    const videoDevice = videoDevices.find((el) => el.label === selectedVideoDevice);
    const audioDeviceIn = audioDevices.find((el) => el.label === selectedAudioDevice);
    if (!videoDevice || !audioDeviceIn || (!selectedAudioOutputDeviceRef.current && !isSafari)) {
      console.log('No all media devices found');
      toast.error('Ошибка при выборе медиа устройств');

      return;
    }

    await stop();

    changeSettings(videoDevice, undefined, undefined); // переключение только видео, т.к. остальные и так переключены

    if (!isPsychologist) {
      localStorage.setItem('video', selectedVideoDevice);
      localStorage.setItem('micro', selectedAudioDevice);
      localStorage.setItem('speaker', selectedAudioOutputDeviceRef.current?.label ?? '');
      toast.success('Данные успешно сохранены');
    } else {
      try {
        const newUserData = await changeUser({
          id: userData?.id,
          settings: [
            {
              video: selectedVideoDevice,
              micro: selectedAudioDevice,
              speaker: selectedAudioOutputDeviceRef.current?.label ?? '',
            },
          ],
        });
        dispatch(setUserData(newUserData?.data || userData));
        localStorage.setItem('video', selectedVideoDevice);
        localStorage.setItem('micro', selectedAudioDevice);
        localStorage.setItem('speaker', selectedAudioOutputDeviceRef.current?.label ?? '');
        toast.success('Данные успешно сохранены');
      } catch (e) {
        console.log(e);
      }
    }

    setOpenSettings(false);
  };

  const updateSpeakers = useCallback(
    async (device: MediaDeviceInfo) => {
      selectedAudioOutputDeviceRef.current = device;

      if (selectedAudioOutputDeviceRef.current && remoteVideoRef?.current && remoteVideoRef?.current?.setSinkId) {
        console.debug(`Updating speakers to ${selectedAudioOutputDeviceRef.current.label}`);

        setSelectedAudioOutputDevice((prevDevice) => {
          return prevDevice?.deviceId === device.deviceId ? prevDevice : device;
        });

        await changeSettings(undefined, selectedAudioOutputDeviceRef.current, undefined);

        remoteVideoRef.current.setSinkId(selectedAudioOutputDeviceRef.current.deviceId);

        console.debug(`Video control is muted ${remoteVideoRef.current.muted}`);
      }
    },
    [changeSettings],
  );

  useEffect(() => {
    const getDevicesAsync = async () => {
      console.debug('Init devices effect');

      if (initialized) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audios = devices.filter((device) => device.kind === 'audioinput');
        const videos = devices.filter((device) => device.kind === 'videoinput');
        const audioOutputs = devices.filter((devices) => devices.kind === 'audiooutput');

        audios?.forEach((d) => {
          console.debug(`Audio with id ${d.deviceId}, label ${d.label}`);
        });

        videos?.forEach((d) => {
          console.debug(`Video with id ${d.deviceId}, label ${d.label}`);
        });

        setAudioDevices(audios);
        setVideoDevices(videos);
        setAudioOutputDevices(audioOutputs);

        let selectedVideo: MediaDeviceInfo | undefined = undefined;
        let selectedAudio: MediaDeviceInfo | undefined = undefined;
        let selectedAudioOutput: MediaDeviceInfo | undefined = undefined;

        if (localStorage.getItem('video')) {
          selectedVideo = videos.find((el) => el.label === localStorage.getItem('video'));

          console.debug(`Found video device ${selectedVideo?.label} from localstorage`);
        }

        if (!selectedVideo && videos) {
          selectedVideo = videos[0];

          console.debug(`Saving video device ${selectedVideo?.label} to localstorage`);
          localStorage.setItem('video', selectedVideo.label);
        }

        if (localStorage.getItem('micro')) {
          selectedAudio = audios.find((el) => el.label === localStorage.getItem('micro'));
          console.debug(`Found microphone ${selectedAudio?.label} from localstorage`);
        }

        if (!selectedAudio && audios) {
          selectedAudio = audios[0];
          console.debug(`Saving microphone device ${selectedAudio?.label} to localstorage`);
          localStorage.setItem('micro', selectedAudio.label);
        }

        if (localStorage.getItem('speaker')) {
          selectedAudioOutput = audioOutputs.find((el) => el.label === localStorage.getItem('speaker'));
          console.debug(`Found speakers ${selectedAudioOutput?.label} from localstorage`);
        }

        if (!selectedAudioOutput && audioOutputs && audioOutputs.length > 0) {
          selectedAudioOutput = audioOutputs[0];
          console.debug(`Saving speakers ${selectedAudioOutput?.label} to localstorage`);
          localStorage.setItem('speaker', selectedAudioOutput.label);
        } else if (isSafari) {
          console.debug('Safari detected — using default speakers');
          localStorage.setItem('speaker', 'Динамики по-умолчанию');
        }

        // Результат не нужен — важен только момент завершения (см. Promise.all ниже).
        let cameraPromise: Promise<unknown> = Promise.resolve();
        let microphonePromise: Promise<unknown> = Promise.resolve();

        if (selectedVideo) {
          console.debug(`Setting video device ${selectedVideo?.label}`);
          cameraPromise = changeVideoDevice(selectedVideo.deviceId);
          setSelectedVideoDevice(selectedVideo.label);
        }

        if (selectedAudio) {
          console.debug(`Setting microphone device ${selectedAudio?.label}`);
          microphonePromise = changeAudioDevice(selectedAudio.deviceId);
          setSelectedAudioDevice(selectedAudio.label);
        }

        if (selectedAudioOutput) {
          console.debug(`Setting speakers ${selectedAudioOutput?.label}`);

          selectedAudioOutputDeviceRef.current = selectedAudioOutput;

          if (remoteVideoRef?.current && remoteVideoRef?.current?.setSinkId) {
            console.debug(`Updating speakers to ${selectedAudioOutputDeviceRef.current.label} from init effect`);

            remoteVideoRef.current.setSinkId(selectedAudioOutputDeviceRef.current.deviceId);
          }

          setSelectedAudioOutputDevice((prevDevice) => {
            return prevDevice?.deviceId === selectedAudioOutput.deviceId ? prevDevice : selectedAudioOutput;
          });
        }

        setIsDevicesInitialized(true);

        await changeSettings(undefined, selectedAudioOutput, selectedAudio);

        await Promise.all([cameraPromise, microphonePromise]);
      }
    };

    getDevicesAsync();
  }, []);

  const toggleAudioMute = () => {
    setIsAudioMuted(!isAudioMuted);
  };

  return (
    <div className="w-full h-full absolute bottom-0 top-0 right-0 left-0 z-modal bg-black/70 flex lg:items-center justify-center sm:p-[15px]">
      <div className="flex think-scroll flex-col w-full h-max pb-5 bg-white w-full max-w-[600px] p-[20px] sm:rounded-popup">
        <div className="w-full flex justify-between mb-[6px] relative">
          <Heading size="lg">Настройки</Heading>

          <CloseButton
            className="absolute right-0 top-0"
            onClick={async () => {
              await stop();

              let selectedVideo: MediaDeviceInfo | undefined = undefined;
              let selectedAudio: MediaDeviceInfo | undefined = undefined;
              let selectedAudioOutput: MediaDeviceInfo | undefined = undefined;

              if (localStorage.getItem('video')) {
                selectedVideo = videoDevices.find((el) => el.label === localStorage.getItem('video'));
              }

              if (localStorage.getItem('micro')) {
                selectedAudio = audioDevices.find((el) => el.label === localStorage.getItem('micro'));
              }

              if (localStorage.getItem('speaker')) {
                selectedAudioOutput = audioOutputDevices.find((el) => el.label === localStorage.getItem('speaker'));
              }

              changeSettings(selectedVideo, selectedAudioOutput, selectedAudio);

              setOpenSettings(false);
            }}
          />
        </div>
        <div className="w-full flex flex-col gap-5">
          <div className="bg-surface-muted p-4 rounded-control">
            <div className="w-full flex flex-col ">
              <p className="text-heading-lg">Видео</p>
              <div className="w-full flex flex-col gap-3">
                <div className="flex flex-col w-full gap-1">
                  <CustomSelect
                    size="lg"
                    selected={selectedVideoDevice}
                    onChange={async (a) => {
                      setSelectedVideoDevice(a);
                      const selectedCamera = videoDevices.find((el) => el.label === a);
                      if (selectedCamera) {
                        await changeVideoDevice(selectedCamera.deviceId);
                      }
                    }}
                    items={videoDevices.map((device) => device.label || `Audio Input ${device.deviceId}`)}
                    isChange={false}
                    readOnly={true}
                    clickToOpenClose={true}
                    disabled={isRemoteLoading}
                  />
                </div>
                {/* Horizontal video previews */}
                <div className="flex gap-[5px]">
                  <div className="relative flex flex-col w-1/2">
                    <video
                      className="w-full h-[150px] bg-line rounded-card object-cover transform scale-x-[-1]"
                      id="settings-video-local"
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted={isAudioMuted}
                    />
                    {isLocalLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-card">
                        <Spinner size="lg" className="text-ink" />
                      </div>
                    )}
                    <p className="text-ui-xs text-center mt-1">Ваша камера</p>
                  </div>
                  <div className="relative flex flex-col w-1/2">
                    <video
                      className="w-full h-[150px] bg-line rounded-card object-cover"
                      id="settings-video"
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      muted={isAudioMuted}
                    />
                    {isRemoteLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-card">
                        <Spinner size="lg" className="text-ink" />
                      </div>
                    )}
                    <p className="text-ui-xs text-center mt-1">Как вас видит собеседник</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-surface-muted p-4 rounded-control">
            <div className="w-full flex flex-col gap-3">
              <p className="text-heading-lg">Аудио</p>
              <div className="w-full flex flex-col gap-3">
                <div className="flex items-center gap-2 w-full">
                  <span className="material-icons">mic</span>
                  <CustomSelect
                    size="lg"
                    selected={selectedAudioDevice}
                    onChange={async (a) => {
                      setSelectedAudioDevice(a);
                      const selectedMicrophone = audioDevices.find((el) => el.label === a);
                      if (selectedMicrophone) {
                        try {
                          await changeSettings(undefined, undefined, selectedMicrophone);
                          await changeAudioDevice(selectedMicrophone.deviceId);
                        } catch (error) {
                          console.error('Failed to change audio device', error);
                        }
                      }
                    }}
                    items={audioDevices.map((device) => device.label || `Audio Input ${device.deviceId}`)}
                    isChange={false}
                    readOnly={true}
                    clickToOpenClose={true}
                    disabled={isRemoteLoading}
                  />
                </div>
                {/* Microphone level indicator */}
                <div className="flex items-center w-full pl-[35px]">
                  <MicrophoneLevelIndicator
                    deviceId={audioDevices.find((d) => d.label === selectedAudioDevice)?.deviceId}
                  />
                </div>
                <div className="flex items-center gap-2 w-full">
                  <span className="material-icons">volume_up</span>
                  <CustomSelect
                    size="lg"
                    selected={selectedAudioOutputDevice?.label || (isSafari ? 'Динамики по-умолчанию' : '')}
                    onChange={async (a) => {
                      if (isSafari && a === 'Динамики по-умолчанию') {
                        setSelectedAudioOutputDevice(undefined);
                        selectedAudioOutputDeviceRef.current = undefined;
                        localStorage.setItem('speaker', 'Динамики по-умолчанию');

                        return;
                      }
                      const audioOutput = audioOutputDevices.find((d) => d.label === a);

                      if (audioOutput) {
                        await changeSettings(undefined, audioOutput, undefined);
                        updateSpeakers(audioOutput);
                      }
                    }}
                    items={[
                      ...(isSafari ? ['Динамики по-умолчанию'] : []),
                      ...audioOutputDevices.map((device) => device.label || `Audio Output ${device.deviceId}`),
                    ]}
                    isChange={false}
                    readOnly={true}
                    clickToOpenClose={true}
                    disabled={isRemoteLoading}
                  />
                </div>
                <Button size="md" variant="neutral" fullWidth onClick={toggleAudioMute}>
                  {!isAudioMuted ? 'Говорите' : 'Проверить звук'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Button size="md" variant="dark" className="ml-auto mt-5 lg:mt-10" onClick={async () => await saveSettings()}>
          Сохранить
        </Button>
      </div>
    </div>
  );
};
